import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import {
  Truck, Euro, TrendingUp, TrendingDown, Loader2, ChevronDown, ChevronRight,
  Pencil, Save, X, Wallet,
} from 'lucide-react'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'

// ── Tipos ─────────────────────────────────────────────────────
interface CarrierCost {
  carrier_code: string; carrier_name: string
  portes: number; fuel: number; reembolso: number; other: number; total: number
  notes?: string | null; invoice_ref?: string | null
}
interface MonthRow {
  year_month: string
  income_factusol: number; income_prestashop: number
  carriers: CarrierCost[]
  total_cost: number; result: number; margin_percent: number; has_data: boolean
}
interface Report {
  ejercicio: string
  months: MonthRow[]
  year_totals: { income_factusol: number; income_prestashop: number; total_cost: number; result: number; margin_percent: number }
  carriers: [string, string][]
}

// ── Helpers ───────────────────────────────────────────────────
const fmtCur = (v: number) => (v || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })
const fmtCur0 = (v: number) => (v || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
const monthShort = (ym: string) => MONTHS_ES[parseInt(ym.slice(5, 7), 10) - 1] || ym
const monthLong = (ym: string) => {
  const names = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  return `${names[parseInt(ym.slice(5, 7), 10) - 1]} ${ym.slice(0, 4)}`
}
const CURRENT_YEAR = 2026
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2]
const CARRIER_LIST: [string, string][] = [['mrw', 'MRW'], ['gls', 'GLS'], ['ctt', 'CTT'], ['cex', 'Correos Express'], ['fedex', 'FedEx'], ['dhl', 'DHL'], ['embalaje', 'Embalaje']]

export default function TransportReportPage() {
  const [ejercicio, setEjercicio] = useState(String(CURRENT_YEAR))
  const [expanded, setExpanded] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)

  const { data, isLoading } = useQuery<Report>({
    queryKey: ['transport-report', ejercicio],
    queryFn: () => api.get(`/transport/report?ejercicio=${ejercicio}`).then(r => r.data),
  })

  const months = data?.months || []
  const yt = data?.year_totals
  const activeMonths = useMemo(() => months.filter(m => m.has_data), [months])
  const chartData = useMemo(() => activeMonths.map(m => ({
    name: monthShort(m.year_month),
    Ingresos: Math.round(m.income_factusol),
    Coste: Math.round(m.total_cost),
    Resultado: Math.round(m.result),
  })), [activeMonths])

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Truck className="w-7 h-7 text-primary-600" />
            Informe de Transportistas
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Ingresos por transporte vs. coste de transportistas — superávit / déficit mensual
          </p>
        </div>
        <select value={ejercicio} onChange={e => setEjercicio(e.target.value)} className="input py-2 pr-8 self-start">
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>
      ) : (
        <>
          {/* KPIs anuales */}
          {yt && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi label={`Ingresos transporte ${ejercicio}`} value={fmtCur(yt.income_factusol)} icon={<Euro className="w-5 h-5" />} color="blue"
                sub={`web (PrestaShop): ${fmtCur(yt.income_prestashop)}`} />
              <Kpi label="Coste transportistas" value={fmtCur(yt.total_cost)} icon={<Wallet className="w-5 h-5" />} color="orange" />
              <Kpi label="Resultado" value={fmtCur(yt.result)} icon={yt.result >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                color={yt.result >= 0 ? 'green' : 'red'} />
              <Kpi label="Margen sobre ingresos" value={`${yt.margin_percent.toFixed(1)}%`} icon={<TrendingUp className="w-5 h-5" />}
                color={yt.result >= 0 ? 'green' : 'red'} />
            </div>
          )}

          {/* Gráfico */}
          {chartData.length > 0 && (
            <div className="card p-4">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Ingresos vs. coste por mes</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v: any) => fmtCur0(Number(v))} width={70} />
                    <Tooltip formatter={(v: any) => fmtCur(Number(v))} />
                    <Legend />
                    <Bar dataKey="Ingresos" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Coste" fill="#f97316" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="Resultado" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Tabla mensual */}
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                <tr className="text-slate-500 dark:text-slate-400">
                  <th className="text-left px-4 py-3 font-medium">Mes</th>
                  <th className="text-right px-4 py-3 font-medium">Ingresos</th>
                  <th className="text-right px-4 py-3 font-medium">Coste transporte</th>
                  <th className="text-right px-4 py-3 font-medium">Resultado</th>
                  <th className="text-right px-4 py-3 font-medium">Margen</th>
                  <th className="px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {activeMonths.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Sin datos para {ejercicio}.</td></tr>
                )}
                {activeMonths.map(m => {
                  const open = expanded === m.year_month
                  return (
                    <>
                      <tr key={m.year_month} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                        <td className="px-4 py-3">
                          <button onClick={() => setExpanded(open ? null : m.year_month)} className="inline-flex items-center gap-1.5 font-medium text-slate-900 dark:text-white">
                            {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                            {monthShort(m.year_month)} {ejercicio}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-900 dark:text-white">{fmtCur(m.income_factusol)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-900 dark:text-white">{fmtCur(m.total_cost)}</td>
                        <td className={`px-4 py-3 text-right tabular-nums font-semibold ${m.result >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                          {m.result >= 0 ? '+' : ''}{fmtCur(m.result)}
                        </td>
                        <td className={`px-4 py-3 text-right tabular-nums ${m.result >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{m.margin_percent.toFixed(1)}%</td>
                        <td className="px-2 py-3 text-right">
                          <button onClick={() => setEditing(m.year_month)} className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded" title="Editar costes del mes">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-slate-50/60 dark:bg-slate-800/30">
                          <td colSpan={6} className="px-4 py-3">
                            <div className="grid md:grid-cols-2 gap-4">
                              {/* Desglose coste por transportista */}
                              <div>
                                <p className="text-xs font-medium text-slate-500 mb-2">Coste por transportista</p>
                                <table className="w-full text-xs">
                                  <thead className="text-slate-400">
                                    <tr><th className="text-left py-1">Transportista</th><th className="text-right">Portes</th><th className="text-right">Combustible</th><th className="text-right">Reembolso</th><th className="text-right">Total</th></tr>
                                  </thead>
                                  <tbody>
                                    {m.carriers.length === 0 && <tr><td colSpan={5} className="py-2 text-slate-400">Sin costes registrados. Pulsa el lápiz para añadirlos.</td></tr>}
                                    {m.carriers.map(c => (
                                      <tr key={c.carrier_code} className="border-t border-slate-100 dark:border-slate-700/40">
                                        <td className="py-1.5 text-slate-700 dark:text-slate-300 font-medium">{c.carrier_name}</td>
                                        <td className="py-1.5 text-right tabular-nums">{fmtCur(c.portes)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{fmtCur(c.fuel)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{fmtCur(c.reembolso)}</td>
                                        <td className="py-1.5 text-right tabular-nums font-semibold text-slate-900 dark:text-white">{fmtCur(c.total)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {/* Cuadre ingresos */}
                              <div>
                                <p className="text-xs font-medium text-slate-500 mb-2">Ingresos por transporte (cuadre)</p>
                                <div className="space-y-1.5 text-xs">
                                  <Row label="Factusol (todos los canales)" value={fmtCur(m.income_factusol)} bold />
                                  <Row label="PrestaShop (solo web)" value={fmtCur(m.income_prestashop)} />
                                  <Row label="No web / facturas directas Factusol" value={fmtCur(m.income_factusol - m.income_prestashop)} muted />
                                  <div className="pt-1.5 mt-1 border-t border-slate-200 dark:border-slate-700">
                                    <Row label="− Coste transportistas" value={`−${fmtCur(m.total_cost)}`} />
                                    <Row label={m.result >= 0 ? 'Superávit' : 'Déficit'} value={`${m.result >= 0 ? '+' : ''}${fmtCur(m.result)}`} bold
                                      className={m.result >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
              {yt && activeMonths.length > 0 && (
                <tfoot className="border-t-2 border-slate-200 dark:border-slate-700 font-semibold">
                  <tr className="text-slate-900 dark:text-white">
                    <td className="px-4 py-3">Total {ejercicio}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtCur(yt.income_factusol)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtCur(yt.total_cost)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${yt.result >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{yt.result >= 0 ? '+' : ''}{fmtCur(yt.result)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${yt.result >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{yt.margin_percent.toFixed(1)}%</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}

      {editing && (
        <EditCostsModal yearMonth={editing} onClose={() => setEditing(null)} ejercicio={ejercicio} />
      )}
    </div>
  )
}

function Row({ label, value, bold, muted, className = '' }: { label: string; value: string; bold?: boolean; muted?: boolean; className?: string }) {
  return (
    <div className={`flex items-center justify-between ${className}`}>
      <span className={muted ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'}>{label}</span>
      <span className={`tabular-nums ${bold ? 'font-semibold' : ''} ${muted ? 'text-slate-400' : 'text-slate-900 dark:text-white'} ${className}`}>{value}</span>
    </div>
  )
}

function Kpi({ label, value, icon, color, sub }: { label: string; value: string; icon: React.ReactNode; color: string; sub?: string }) {
  const colors: Record<string, string> = {
    blue: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400',
    orange: 'text-orange-600 bg-orange-50 dark:bg-orange-900/30 dark:text-orange-400',
    green: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400',
    red: 'text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-400',
  }
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[color]}`}>{icon}</span>
      </div>
      <p className="text-xl font-bold text-slate-900 dark:text-white mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  )
}

// ── Modal de edición de costes ────────────────────────────────
function EditCostsModal({ yearMonth, onClose, ejercicio }: { yearMonth: string; onClose: () => void; ejercicio: string }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<{ carriers: CarrierCost[] }>({
    queryKey: ['transport-costs', yearMonth],
    queryFn: () => api.get(`/transport/costs/${yearMonth}`).then(r => r.data),
  })
  const [form, setForm] = useState<Record<string, { portes: string; fuel: string; reembolso: string }>>({})

  // Inicializa el formulario al cargar
  useMemo(() => {
    if (!data) return
    const byCode: Record<string, CarrierCost> = {}
    for (const c of data.carriers) byCode[c.carrier_code] = c
    const init: Record<string, { portes: string; fuel: string; reembolso: string }> = {}
    for (const [code] of CARRIER_LIST) {
      const c = byCode[code]
      init[code] = { portes: c ? String(c.portes) : '', fuel: c ? String(c.fuel) : '', reembolso: c ? String(c.reembolso) : '' }
    }
    setForm(init)
  }, [data])

  const saveMut = useMutation({
    mutationFn: () => {
      // Se conservan other/notes/invoice_ref ya guardados (p. ej. abonos o notas de origen de la factura)
      const existing: Record<string, CarrierCost> = {}
      for (const c of data?.carriers ?? []) existing[c.carrier_code] = c
      const carriers = CARRIER_LIST.map(([code]) => ({
        carrier_code: code,
        portes: parseFloat(form[code]?.portes) || 0,
        fuel: parseFloat(form[code]?.fuel) || 0,
        reembolso: parseFloat(form[code]?.reembolso) || 0,
        other: existing[code]?.other ?? 0,
        notes: existing[code]?.notes ?? null,
        invoice_ref: existing[code]?.invoice_ref ?? null,
      })).filter(c => c.portes || c.fuel || c.reembolso || c.other)
      return api.put(`/transport/costs/${yearMonth}`, { carriers })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transport-report', ejercicio] })
      qc.invalidateQueries({ queryKey: ['transport-costs', yearMonth] })
      onClose()
    },
  })

  const upd = (code: string, field: 'portes' | 'fuel' | 'reembolso', v: string) =>
    setForm(f => ({ ...f, [code]: { ...f[code], [field]: v } }))

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-semibold text-slate-900 dark:text-white">Costes de transporte — {monthLong(yearMonth)}</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-slate-400 text-xs">
                <tr><th className="text-left pb-2">Transportista</th><th className="text-right pb-2">Portes (€)</th><th className="text-right pb-2">Combustible</th><th className="text-right pb-2">Reembolso</th></tr>
              </thead>
              <tbody>
                {CARRIER_LIST.map(([code, name]) => (
                  <tr key={code}>
                    <td className="py-1.5 font-medium text-slate-700 dark:text-slate-300">{name}</td>
                    {(['portes', 'fuel', 'reembolso'] as const).map(field => (
                      <td key={field} className="py-1.5 pl-2">
                        <input type="number" step="0.01" value={form[code]?.[field] ?? ''}
                          onChange={e => upd(code, field, e.target.value)}
                          className="w-full px-2 py-1 text-right border rounded-md dark:bg-slate-700 dark:border-slate-600 tabular-nums" placeholder="0" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancelar</button>
          <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 inline-flex items-center gap-1.5">
            {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Guardar
          </button>
        </div>
      </div>
    </div>
  )
}
